/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/

/**
 * Menu V3 RTK Query hooks — injected into menuApi
 *
 * Uses foodMerchantBaseApi.injectEndpoints so that V3 queries share the same API slice
 * as menuApi, enabling native cross-invalidation (e.g. deleteItemV2 can
 * invalidate menuV3Items / menuV3Hierarchies without a manual dispatch).
 */

import { InfiniteData } from "@reduxjs/toolkit/query"
import {
  FoodmaxapipbMenuV3ModifierGroupsResponse,
  FoodmaxapipbMenuV3HierarchiesResponse,
  FoodmaxapipbMenuV3ItemsResponse,
  FoodmaxapipbMenuV3EditorStatusResponse,
} from "generated/foodmaxapi.type"
import { createRequestHeaders } from "./apiClient"
import { endpoints, CACHE_QUERY_TAGS } from "./constants"
import {
  MenuV3ModifierGroupsRequest,
  MenuV3HierarchiesRequest,
  MenuV3ItemsRequest,
  MenuV3EditorStatusRequest,
} from "./types"
import { foodMerchantBaseApi } from "./baseApis"

export const DEFAULT_LIMIT = 100
export const DEFAULT_MODIFIER_GROUP_LIMIT = 50

// ============================================================================
// V3 endpoints injected into menuApi
// ============================================================================

export const partialMenuApi = foodMerchantBaseApi.injectEndpoints({
  endpoints: builder => ({
    /**
     * Fetch Menu V3 Editor Status
     */
    menuV3EditorStatus: builder.query<
      FoodmaxapipbMenuV3EditorStatusResponse,
      MenuV3EditorStatusRequest
    >({
      query: ({ merchantID, merchantGroupID }) => ({
        url: endpoints.menuV3EditorStatus,
        headers: createRequestHeaders({ merchantID, merchantGroupID }),
      }),
    }),

    /**
     * Fetch Menu V3 Hierarchies with native infinite query
     */
    menuV3Hierarchies: builder.infiniteQuery<
      FoodmaxapipbMenuV3HierarchiesResponse,
      MenuV3HierarchiesRequest,
      number
    >({
      infiniteQueryOptions: {
        initialPageParam: 0,
        getNextPageParam: (lastPage, _allPages, lastPageParam) =>
          lastPage.hasMore ? lastPageParam + 1 : undefined,
      },
      query: ({ queryArg, pageParam }) => ({
        url: endpoints.menuV3Hierarchies,
        headers: createRequestHeaders({
          merchantID: queryArg.merchantID,
          merchantGroupID: queryArg.merchantGroupID,
        }),
        params: {
          limit: queryArg.limit || DEFAULT_LIMIT,
          pageIndex: pageParam,
          parentType: queryArg.parentType,
          parentID: queryArg.parentID,
        },
      }),
      providesTags: [CACHE_QUERY_TAGS.menuV3Hierarchies],
    }),

    /**
     * Fetch Menu V3 Modifier Groups with native infinite query
     */
    menuV3ModifierGroups: builder.infiniteQuery<
      FoodmaxapipbMenuV3ModifierGroupsResponse,
      MenuV3ModifierGroupsRequest,
      number
    >({
      infiniteQueryOptions: {
        initialPageParam: 0,
        getNextPageParam: (lastPage, _allPages, lastPageParam) =>
          lastPage.hasMore ? lastPageParam + 1 : undefined,
      },
      query: ({ queryArg, pageParam }) => ({
        url: endpoints.menuV3ModifierGroups,
        headers: createRequestHeaders({
          merchantID: queryArg.merchantID,
          merchantGroupID: queryArg.merchantGroupID,
        }),
        params: {
          limit: queryArg.limit || DEFAULT_MODIFIER_GROUP_LIMIT,
          pageIndex: pageParam,
        },
      }),
      providesTags: [CACHE_QUERY_TAGS.menuV3ModifierGroups],
    }),

    /**
     * Fetch Menu V3 Items with native infinite query
     */
    menuV3Items: builder.infiniteQuery<
      FoodmaxapipbMenuV3ItemsResponse,
      MenuV3ItemsRequest,
      number
    >({
      infiniteQueryOptions: {
        initialPageParam: 0,
        getNextPageParam: (lastPage, _allPages, lastPageParam) =>
          lastPage.hasMore ? lastPageParam + 1 : undefined,
      },
      query: ({ queryArg, pageParam }) => {
        // Flatten filter object into query params with "filter." prefix
        const filterParams: Record<string, any> = {}
        if (queryArg.filter) {
          Object.entries(queryArg.filter).forEach(([key, value]) => {
            if (value !== undefined) {
              filterParams[`filter.${key}`] = value
            }
          })
        }

        return {
          url: endpoints.menuV3Items,
          headers: createRequestHeaders({
            merchantID: queryArg.merchantID,
            merchantGroupID: queryArg.merchantGroupID,
          }),
          params: {
            limit: queryArg.limit || DEFAULT_LIMIT,
            pageIndex: pageParam,
            parentType: queryArg.parentType,
            parentID: queryArg.parentID,
            ...filterParams,
          },
        }
      },
      providesTags: [CACHE_QUERY_TAGS.menuV3Items],
    }),
  }),
})

// ============================================================================
// Exported Hooks
// ============================================================================

export const {
  // Simple query
  useMenuV3EditorStatusQuery,
  // Native infinite queries (built-in fetchNextPage, hasNextPage, etc.)
  useMenuV3HierarchiesInfiniteQuery: useMenuV3HierarchiesInfinite,
  useMenuV3ModifierGroupsInfiniteQuery: useMenuV3ModifierGroupsInfinite,
  useMenuV3ItemsInfiniteQuery: useMenuV3ItemsInfinite,
} = partialMenuApi

// ============================================================================
// Helper to flatten pages (like React Query's data.pages.flat())
// ============================================================================

export function flattenInfiniteItems(
  data: InfiniteData<FoodmaxapipbMenuV3ItemsResponse, number> | undefined,
) {
  if (!data?.pages) return []
  return data.pages.flatMap(page => page.items || [])
}

export function flattenInfiniteHierarchies(
  data: InfiniteData<FoodmaxapipbMenuV3HierarchiesResponse, number> | undefined,
) {
  if (!data?.pages) return []
  return data.pages.flatMap(page => page.hierarchies || [])
}

export function flattenInfiniteModifierGroups(
  data:
    | InfiniteData<FoodmaxapipbMenuV3ModifierGroupsResponse, number>
    | undefined,
) {
  if (!data?.pages) return []
  return data.pages.flatMap(page => page.modifierGroups || [])
}
